import { Route, Routes, useLocation, Outlet, useParams } from "react-router-dom";
import { useEffect } from "react";

import LoginPage from "@/pages/LoginPage";
import NotFound from "@/pages/NotFound";
import { SetupRoutes } from "./SetupRoutes";
import { useGuardBuildSteps } from "@/hooks/guards/useGuardBuildSteps";
import { GuardedRoute } from "@/components/routing/GuardedRoute";
import { useGuardWorkflow } from "@/hooks/guards/useGuardWorkflow";
import { useGuardWorkspace } from "@/hooks/guards/useGuardWorkspace";
import { useGuardNoWorkspace } from "@/hooks/guards/useGuardNoWorkspace";
import { HydrateWorkflow } from "@/hydrate/HydrateWorkflow";
import { HydratePublicWorkflow } from "@/hydrate/HydratePublicWorkflow";
import { useGuardUpload } from "@/hooks/guards/useGuardUpload";
import { UploadVisualAreaContent } from "@/components/flow/build/Steps/Upload/VisualAreaContent";
import { BuildPage } from "@/pages/BuildPage";
import { SharePage } from "@/pages/SharePage";
import { WorkflowPage } from "@/pages/WorkflowPage";
import { ModelsPage } from "@/pages/ModelsPage";
import { AnimatePresence } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { TutorialCard } from "@/components/tutorial/TutorialCard";
import { RapidDataIdRedirect } from "@/pages/RapidDataIdRedirect";
import { WorkpaceIdWorkflowIdRedirect } from "@/pages/WorkpaceIdWorkflowIdRedirect";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsPageView } from "@/analytics/types";
import { StepToAnalyticsPageViewMap } from "@/analytics/types";
import { useFlowState } from "@/hooks/useFlowState";
import { useAuth } from "@/contexts/AuthContext";
import {
    useCurrentWorkspace,
    useFetchWorkspaces,
    useSwitchWorkspace,
    useWorkspaceLoading,
    useWorkspaces,
    useWorkspacesHasAPIFetched
} from "@/store";
import useApi from "@/hooks/useApi";
import { RapidDataInitializer } from "@/components/RapidDataInitializer";

function AppLayout() {
    const { flow, step } = useFlowState();
    const location = useLocation();
    const { currentPage, setCurrentPage } = useAnalytics();

    useEffect(() => {
        if (currentPage.path !== location.pathname) {
            const pageName = !(flow && step)
                ? AnalyticsPageView.HOME
                : StepToAnalyticsPageViewMap[step];
            setCurrentPage({ name: pageName, path: location.pathname });
        }
    }, [flow, location.pathname, step, currentPage, setCurrentPage]);

    const { user, activeSession, apiAuthenticated } = useAuth();
    const api = useApi();
    const fetchWorkspaces = useFetchWorkspaces();
    const workspaceLoading = useWorkspaceLoading();
    const workspaces = useWorkspaces();
    const switchWorkspace = useSwitchWorkspace();
    const workspacesHasAPIFetched = useWorkspacesHasAPIFetched();
    const currentWorkspace = useCurrentWorkspace();
    const { workspaceUrl } = useParams<{ workspaceUrl: string }>();

    // Initialize/Hydate workspaces when user becomes authenticated
    useEffect(() => {
        if (user && activeSession && !workspaceLoading && !workspacesHasAPIFetched) {
            fetchWorkspaces(api, user, workspaceUrl || null);
        }
    }, [
        user,
        apiAuthenticated,
        fetchWorkspaces,
        activeSession,
        workspaceLoading,
        workspacesHasAPIFetched,
        workspaceUrl,
        api
    ]);

    // when the workspaceURl changes, switch to that workspace
    useEffect(() => {
        if (workspaceUrl && currentWorkspace && workspaceUrl !== currentWorkspace.url) {
            const newWorkspace = workspaces?.find((w) => {
                return w.url === workspaceUrl;
            });
            if (newWorkspace) {
                switchWorkspace(newWorkspace);
            }
        }
    }, [workspaceUrl, currentWorkspace, workspaces, switchWorkspace]);

    return (
        <AnimatePresence mode="wait" initial={false}>
            <div className="flex h-dvh flex-col overflow-hidden bg-white md:overflow-y-auto">
                <Navbar />
                <TutorialCard />
                <RapidDataInitializer />
                <main className="flex-1 border-t">
                    <Outlet />
                </main>
            </div>
        </AnimatePresence>
    );
}

export function AppRoutes() {
    const location = useLocation();

    // Note: key={location.pathname} forces React to remount the entire Routes tree on navigation
    // This prevents hook order violations when switching between routes with different guards
    return (
        <Routes location={location} key={location.pathname}>
            <Route element={<AppLayout />}>
                <Route path="/redirect">
                    <Route path="id/:rapidDataId" element={<RapidDataIdRedirect />} />
                    <Route
                        path="workspace-id/:workspaceId/workflow-id/:workflowId"
                        element={<WorkpaceIdWorkflowIdRedirect />}
                    />
                </Route>
                <Route
                    element={
                        <GuardedRoute useGuardStep={useGuardNoWorkspace}>
                            <Outlet />
                        </GuardedRoute>
                    }
                >
                    <Route path="/login" element={<LoginPage />} />
                    {/* <Route path="/logout" element={<LogoutPage />} /> */}

                    <Route
                        path="/"
                        element={
                            <GuardedRoute useGuardStep={useGuardUpload}>
                                <UploadVisualAreaContent />
                            </GuardedRoute>
                        }
                    />
                    {/* Share routes with /p/ prefix - redirects to workspace-scoped route if logged in */}
                    <Route
                        path="p/:shareableSlug"
                        element={
                            <HydratePublicWorkflow>
                                <SharePage />
                            </HydratePublicWorkflow>
                        }
                    />
                    <Route
                        path="build/:step"
                        element={
                            <GuardedRoute useGuardStep={useGuardBuildSteps}>
                                <BuildPage />
                            </GuardedRoute>
                        }
                    />
                </Route>
                <Route
                    path=":workspaceUrl/*"
                    element={
                        <GuardedRoute useGuardStep={useGuardWorkspace}>
                            <Outlet />
                        </GuardedRoute>
                    }
                >
                    <Route path="models" element={<ModelsPage />} />
                    <Route
                        path="p/:shareableSlug"
                        element={
                            <HydratePublicWorkflow>
                                <SharePage />
                            </HydratePublicWorkflow>
                        }
                    />
                    <Route
                        path="build/:step"
                        element={
                            <GuardedRoute useGuardStep={useGuardBuildSteps}>
                                <BuildPage />
                            </GuardedRoute>
                        }
                    />
                    <Route
                        path=":workflowUrl/:step/:sourceId?"
                        element={
                            <HydrateWorkflow>
                                <GuardedRoute useGuardStep={useGuardWorkflow}>
                                    <WorkflowPage />
                                </GuardedRoute>
                            </HydrateWorkflow>
                        }
                    />
                    <Route path="*" element={<SetupRoutes />} />
                </Route>
                <Route path="*" element={<NotFound />} />
            </Route>
        </Routes>
    );
}
